'use client';
import { type ReactNode, useEffect, useRef } from 'react';

import { usePageLogger } from '../hooks/usePageLogger';
import { type LogParameters } from '../models';

interface Props {
  params?: LogParameters;
  children: ReactNode;
}

export const LoggingView = ({ params, children }: Props) => {
  const logger = usePageLogger();
  const lastNavKeyRef = useRef<string | undefined>(undefined);
  const hasFiredOnMountRef = useRef(false);
  const paramsRef = useRef(params);

  useEffect(() => {
    paramsRef.current = params;
  });

  // Nav-scoped LoggingView (e.g., layout.client.tsx) passes `page_location` so we
  // dedup by that. Ecommerce-scoped LoggingView (e.g., checkout/PDP view_item) does
  // not pass page_location — for those we fall back to single-fire-on-mount to keep
  // existing GA4 ecommerce events (view_item, begin_checkout) firing.
  const navKey = typeof params?.page_location === 'string' ? params.page_location : undefined;

  useEffect(() => {
    // Guard against undefined logger (missing provider) and uninitialized state.
    // Without the `!logger` check, refs would be marked as fired even though
    // logger?.view(...) is a no-op, causing the real fire to be skipped once
    // the logger becomes available.
    if (!logger || logger.isInitialized === false) {
      return;
    }

    // Ecommerce (no page_location): fire once per mount.
    if (!navKey) {
      if (hasFiredOnMountRef.current) {
        return;
      }
      logger.view(paramsRef.current);
      hasFiredOnMountRef.current = true;
      return;
    }

    // Navigation (has page_location): dedup by navKey so re-renders of the same
    // URL don't refire, and read document.title fresh (usePageInfo state may lag).
    if (lastNavKeyRef.current === navKey) {
      return;
    }
    const latestTitle = (() => {
      if (typeof document === 'undefined') {
        return paramsRef.current?.page_title;
      }
      if (!document.title) {
        return paramsRef.current?.page_title;
      }
      return document.title;
    })();
    logger.view({
      ...paramsRef.current,
      page_title: latestTitle,
    });
    lastNavKeyRef.current = navKey;
  }, [logger, navKey]);

  return <>{children}</>;
};
